import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain } from "@/components/Common";
import Glossy3DCard from "@/components/Glossy3DCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeftRight, Loader2, Search, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Migration() {
  const { selected } = useDomains();
  const did = selected?.id;
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [fileId, setFileId] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!selected) return <NoDomain />;

  const listFiles = async () => {
    if (!source) { toast.error("Enter source user email"); return; }
    setLoading(true);
    try { const { data } = await api.get(`/domains/${did}/users/${source}/files`); setFiles(data); if (!data.length) toast.info("No files owned by this user"); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const transfer = async (fid) => {
    if (!target) { toast.error("Enter new owner email"); return; }
    setBusy(true);
    try { await api.post(`/domains/${did}/transfer`, { source_email: source, new_owner: target, file_id: fid }); toast.success("Ownership transferred"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="File Ownership Migration" subtitle={`${selected.domain} · same-organization transfers`} icon={ArrowLeftRight} />
      <div className="grid gap-4 lg:grid-cols-3">
        <Glossy3DCard tilt={false} className="p-6 lg:col-span-1">
          <span className="spec-shine" />
          <h3 className="font-heading mb-4 text-lg font-semibold text-slate-800">Transfer settings</h3>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Source user (current owner)</Label><Input value={source} onChange={(e) => setSource(e.target.value)} placeholder={`old@${selected.domain}`} data-testid="source-email-input" /></div>
            <div className="space-y-1.5"><Label>New owner</Label><Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder={`new@${selected.domain}`} data-testid="target-email-input" /></div>
            <Button onClick={listFiles} disabled={loading} className="glossy-emerald relative w-full rounded-xl" data-testid="list-files-button"><span className="spec-shine" />{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1 h-4 w-4" />List source files</>}</Button>
            <div className="space-y-1.5 border-t border-white/60 pt-3"><Label>Or transfer a single file by ID</Label>
              <div className="flex gap-2"><Input value={fileId} onChange={(e) => setFileId(e.target.value)} placeholder="Drive file ID" data-testid="file-id-input" />
                <Button onClick={() => transfer(fileId)} disabled={busy || !fileId} variant="outline" className="rounded-xl bg-white/60" data-testid="migrate-ownership-submit-button">Go</Button></div>
            </div>
            <p className="text-xs text-slate-400">Note: cross-organization transfers and shared-drive items are not supported by Google's API.</p>
          </div>
        </Glossy3DCard>

        <div className="glass rounded-3xl p-4 lg:col-span-2">
          <h3 className="font-heading mb-3 text-lg font-semibold text-slate-800">Owned files</h3>
          {files.length === 0 ? <p className="rounded-2xl bg-white/50 p-8 text-center text-sm text-slate-500">List a user's files to transfer them individually.</p> :
            <div className="space-y-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded-2xl bg-white/60 p-3" data-testid={`file-${f.id}`}>
                  <div className="flex items-center gap-3 truncate"><FileText className="h-4 w-4 shrink-0 text-emerald-600" /><div className="truncate"><p className="truncate text-sm font-medium text-slate-800">{f.name}</p><p className="truncate font-mono text-xs text-slate-400">{f.id}</p></div></div>
                  <Button size="sm" onClick={() => transfer(f.id)} disabled={busy} className="glossy-emerald relative shrink-0 rounded-lg" data-testid={`transfer-${f.id}`}><span className="spec-shine" />Transfer</Button>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}
